 import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

export default function PubSubInfographic() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [eventSent, setEventSent] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setEventSent(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  return (
    <div ref={ref} className="relative w-full">
      {/* Friendly explanation */}
      <div className="mb-8 bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <span className="text-2xl">📡</span> What's Pub/Sub?
        </h3>
        <p className="text-gray-600 leading-relaxed">
          <strong>Publish/Subscribe</strong> is like a newspaper delivery. Instead of everyone 
          going to the newsstand to check for news (polling), subscribers <strong>get the paper 
          delivered</strong> the moment it's printed!
        </p>
        <ul className="mt-3 space-y-2 text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span><strong>Publisher</strong>: The auction (announces events)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span><strong>Subscribers</strong>: Bots, dashboards, anything interested</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span>No polling—events are <strong>pushed instantly</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span>Scales to thousands of subscribers</span>
          </li>
        </ul>
      </div>

      {/* Before/After comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* BAD: Polling */}
        <div className="bg-white rounded-2xl border border-red-200 p-6">
          <h4 className="text-sm font-semibold text-red-600 mb-4 flex items-center gap-2">
            <span>❌</span> Before: Bots Polling
          </h4>
          <svg viewBox="0 0 200 200" className="w-full h-auto">
            <rect x="0" y="0" width="200" height="200" fill="#fef2f2" rx="8" />
            
            {/* Auction */}
            <rect x="70" y="20" width="60" height="50" rx="8" fill="#fee2e2" stroke="#dc2626" strokeWidth="2" />
            <text x="100" y="45" textAnchor="middle" fontSize="18">🏷️</text>
            <text x="100" y="62" textAnchor="middle" fontSize="8" fill="#991b1b">Auction</text>

            {/* Bots polling */}
            {[
              { x: 30, y: 120, emoji: '🤖', name: 'Bot 1' },
              { x: 85, y: 140, emoji: '🤖', name: 'Bot 2' },
              { x: 140, y: 120, emoji: '🤖', name: 'Bot 3' },
            ].map((bot, i) => (
              <g key={i}>
                <circle cx={bot.x + 15} cy={bot.y} r="20" fill="#fee2e2" stroke="#fca5a5" strokeWidth="1" />
                <text x={bot.x + 15} y={bot.y + 5} textAnchor="middle" fontSize="14">{bot.emoji}</text>
                <text x={bot.x + 15} y={bot.y + 30} textAnchor="middle" fontSize="7" fill="#6b7280">{bot.name}</text>
                
                {/* Polling arrows */}
                <motion.g
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 0.8, delay: i * 0.2, repeat: Infinity }}
                >
                  <line x1={bot.x + 15} y1={bot.y - 20} x2="100" y2="75" stroke="#dc2626" strokeWidth="1" strokeDasharray="3 2" />
                </motion.g>
              </g>
            ))}

            <text x="100" y="185" textAnchor="middle" fontSize="8" fill="#991b1b" fontWeight="500">
              "Any updates?" every second 😵
            </text>
          </svg>
        </div>

        {/* GOOD: Pub/Sub */}
        <div className="bg-white rounded-2xl border border-green-200 p-6">
          <h4 className="text-sm font-semibold text-green-600 mb-4 flex items-center gap-2">
            <span>✅</span> After: Pub/Sub Lite
          </h4>
          <svg viewBox="0 0 200 200" className="w-full h-auto">
            <rect x="0" y="0" width="200" height="200" fill="#f0fdf4" rx="8" />
            
            {/* Auction (Publisher) */}
            <rect x="70" y="20" width="60" height="50" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" />
            <text x="100" y="45" textAnchor="middle" fontSize="18">🏷️</text>
            <text x="100" y="62" textAnchor="middle" fontSize="8" fill="#166534">Publisher</text>

            {/* Push arrows - animate when event sent */}
            {[
              { x: 45, endX: 45 },
              { x: 100, endX: 100 },
              { x: 155, endX: 155 },
            ].map((arrow, i) => (
              <motion.g key={i}>
                <motion.line
                  x1="100"
                  y1="75"
                  x2={arrow.endX}
                  y2="95"
                  stroke="#22c55e"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={eventSent ? { pathLength: 1 } : {}}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                />
                <motion.polygon
                  points={`${arrow.endX},100 ${arrow.endX - 4},92 ${arrow.endX + 4},92`}
                  fill="#22c55e"
                  initial={{ opacity: 0 }}
                  animate={eventSent ? { opacity: 1 } : {}}
                  transition={{ delay: 0.5 + i * 0.1 }}
                />
              </motion.g>
            ))}

            {/* Event message */}
            {eventSent && (
              <motion.g
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <rect x="60" y="78" width="80" height="16" rx="4" fill="#22c55e" />
                <text x="100" y="89" textAnchor="middle" fontSize="7" fill="white" fontWeight="500">
                  📢 New Bid: £100
                </text>
              </motion.g>
            )}

            {/* Bots (Subscribers) */}
            {[
              { x: 30, y: 130, emoji: '🤖', name: 'Bot 1' },
              { x: 85, y: 150, emoji: '🤖', name: 'Bot 2' },
              { x: 140, y: 130, emoji: '🤖', name: 'Bot 3' },
            ].map((bot, i) => (
              <motion.g 
                key={i}
                animate={eventSent ? { scale: [1, 1.1, 1] } : {}}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.3 }}
              >
                <circle cx={bot.x + 15} cy={bot.y} r="20" fill="#dcfce7" stroke="#86efac" strokeWidth="1" />
                <text x={bot.x + 15} y={bot.y + 5} textAnchor="middle" fontSize="14">{bot.emoji}</text>
                <text x={bot.x + 15} y={bot.y + 30} textAnchor="middle" fontSize="7" fill="#166534">Subscriber</text>
              </motion.g>
            ))}

            <text x="100" y="195" textAnchor="middle" fontSize="8" fill="#166534" fontWeight="500">
              Events pushed instantly 🚀
            </text>
          </svg>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h4 className="text-sm font-semibold text-gray-500 mb-4">How Pub/Sub Lite Works</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-purple-50 rounded-xl p-4 text-center"
          >
            <div className="text-3xl mb-2">1️⃣</div>
            <h5 className="font-semibold text-gray-900 mb-1">Subscribe</h5>
            <p className="text-sm text-gray-600">
              Bot tells auction: "Let me know when something happens"
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="bg-blue-50 rounded-xl p-4 text-center"
          >
            <div className="text-3xl mb-2">2️⃣</div>
            <h5 className="font-semibold text-gray-900 mb-1">Event Happens</h5>
            <p className="text-sm text-gray-600">
              Someone places a bid on the auction
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6 }}
            className="bg-green-50 rounded-xl p-4 text-center"
          >
            <div className="text-3xl mb-2">3️⃣</div>
            <h5 className="font-semibold text-gray-900 mb-1">Push to All</h5>
            <p className="text-sm text-gray-600">
              Auction tells <strong>all subscribers</strong> immediately
            </p>
          </motion.div>
        </div>

        {/* Replay button */}
        <div className="mt-4 text-center">
          <button
            onClick={() => { setEventSent(false); setTimeout(() => setEventSent(true), 500); }}
            className="text-sm bg-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-200 transition-colors"
          >
            🔄 Replay Animation
          </button>
        </div>
      </div>

      {/* Comparison stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-red-600">1000+</div>
          <div className="text-xs text-gray-500">Polling requests/min</div>
          <div className="text-xs text-red-500 font-medium">Before</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-green-600">~5</div>
          <div className="text-xs text-gray-500">Push events/min</div>
          <div className="text-xs text-green-500 font-medium">After</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-red-600">1-2s</div>
          <div className="text-xs text-gray-500">Delay with polling</div>
          <div className="text-xs text-red-500 font-medium">Before</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-green-600">&lt;50ms</div>
          <div className="text-xs text-gray-500">Instant push</div>
          <div className="text-xs text-green-500 font-medium">After</div>
        </div>
      </div>

      {/* Real-world analogy */}
      <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
        <div className="flex items-start gap-3">
          <span className="text-2xl">📰</span>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Think of a Newspaper Subscription</h4>
            <p className="text-sm text-gray-600">
              <strong>Polling</strong> is driving to the newsstand every hour to check for the evening edition.
              <strong> Pub/Sub</strong> is having the paper <strong>delivered to your door</strong> the moment 
              it's printed. Same news, way less effort!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
